import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { ChatDetail } from 'types/api'

import { Button } from '@/components/ui/button'
import { useUpdateChat } from '@services/chat/mutations/useUpdateChat'
import { useProfile } from '@services/users/queries/useProfile'

import { ChatBubble } from './ChatBubble'
import { ChatComposer } from './ChatComposer'

export function ChatThread({ data }: { data: ChatDetail }) {
  const { t } = useTranslation('chat')
  const { data: profile } = useProfile(true)
  const updateChat = useUpdateChat()
  const messagesRef = useRef<HTMLDivElement>(null)

  const messages = data.messages ?? []
  // In a support chat everyone except an admin is on the "USER" side
  // (USER, ROOMMATE, LANDLORD). A message is "own" when it matches that side.
  const ownType = profile?.role === 'ADMIN' ? 'ADMIN' : 'USER'

  useEffect(() => {
    const id = setTimeout(() => {
      messagesRef.current?.scrollTo({
        top: messagesRef.current.scrollHeight,
        behavior: 'smooth'
      })
    }, 250)
    return () => clearTimeout(id)
  }, [data])

  const reactivate = () => {
    if (!data.id) return
    updateChat.mutate({
      id: data.id,
      body: { title: data.title, offer: data.offer, is_active: true }
    })
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-background">
      <div
        ref={messagesRef}
        className="min-h-0 flex-1 space-y-2 overflow-y-auto px-4 py-3"
      >
        {messages.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t('form.common.no_messages')}
          </p>
        ) : (
          messages.map((m) => (
            <ChatBubble key={m.id} message={m} own={m.type === ownType} />
          ))
        )}

        {!data.is_active && (
          <div className="flex flex-col items-center gap-3 rounded-2xl bg-muted/60 p-4 text-center">
            <p className="text-sm text-muted-foreground">
              {t('form.common.ended_chat')}
            </p>
            <Button variant="outline" size="sm" onClick={reactivate}>
              {t('form.common.update_chat')}
            </Button>
          </div>
        )}
      </div>

      {data.is_active && data.id && <ChatComposer chatId={data.id} />}
    </div>
  )
}
